import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Icon } from "@/lib/icons";
import { isValidExcelWorkbook } from "./excel-file-monitor";

interface LanguageOption {
  code: string;
  name: string;
}

interface ImportCopy {
  title: string;
  description: string;
  language: string;
  selectLanguage: string;
  file: string;
  noLanguages: string;
  fileRequirements: string;
  invalidFile: string;
  cancel: string;
  submit: string;
  submitting: string;
}

interface ExcelFileImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  languages: LanguageOption[];
  language: string;
  file: File | null;
  copy: ImportCopy;
  isReady: boolean;
  isSubmitting: boolean;
  onLanguageChange: (language: string) => void;
  onFileChange: (file: File | null) => void;
  onSubmit: () => void;
}

export function ExcelFileImportDialog(props: ExcelFileImportDialogProps) {
  const hasInvalidFile = props.file !== null && !isValidExcelWorkbook(props.file);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="border-border/40 bg-card sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{props.copy.title}</DialogTitle>
          <DialogDescription>{props.copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label>{props.copy.language}</Label>
            <Select
              value={props.language}
              onValueChange={props.onLanguageChange}
              disabled={props.isSubmitting || props.languages.length === 0}
            >
              <SelectTrigger className="border-border/40 bg-muted/20">
                <SelectValue placeholder={props.copy.selectLanguage} />
              </SelectTrigger>
              <SelectContent>
                {props.languages.map((language) => (
                  <SelectItem key={language.code} value={language.code}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {props.languages.length === 0 && (
              <p className="text-xs text-destructive">{props.copy.noLanguages}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="initial-data-phrase-import">{props.copy.file}</Label>
            <Input
              id="initial-data-phrase-import"
              type="file"
              accept=".xlsx,.xls"
              disabled={props.isSubmitting}
              onChange={(event) => props.onFileChange(event.target.files?.[0] ?? null)}
              className="border-border/40 bg-muted/20"
            />
            {props.file && (
              <p className="truncate text-xs text-muted-foreground">{props.file.name}</p>
            )}
            <p className={hasInvalidFile ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
              {hasInvalidFile ? props.copy.invalidFile : props.copy.fileRequirements}
            </p>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => props.onOpenChange(false)}
            disabled={props.isSubmitting}
            className="rounded-full border border-border/40 px-4 py-2 text-xs font-semibold text-muted-foreground"
          >
            {props.copy.cancel}
          </button>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={!props.isReady || props.isSubmitting}
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background disabled:opacity-40"
          >
            <Icon
              name={props.isSubmitting ? "loader2" : "upload"}
              className={`h-3.5 w-3.5 ${props.isSubmitting ? "animate-spin" : ""}`}
            />
            {props.isSubmitting ? props.copy.submitting : props.copy.submit}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

